// Import semestriel de GeoNames (villes de plus de 1 000 habitants, CC BY 4.0) pour placer les fiches sur la carte :
// le serveur de téléchargement refuse les robots, le responsable des imports télécharge cities1000.zip à la main.
// Le navigateur lit le ZIP et n'envoie que la forme compacte de chaque ville, que le worker range dans `geo_places`
// (apps/worker/src/radar/geocode.py, même ordre des champs).
import { Unzip, UnzipInflate } from "fflate";

export const GEONAMES_DUMP = "https://download.geonames.org/export/dump/";
const DUE_DAY = 6;

/** [identifiant, nom, nom ASCII, latitude, longitude, pays, région, population, noms alternatifs latins] */
export type CompactCity = [
  number,
  string,
  string,
  number,
  number,
  string,
  string,
  number,
  string[],
];

// Noms en alphabet latin seulement : les fiches nomment leur ville en lettres latines.
const LATIN = /^[\p{Script=Latin}\p{M}\s'’.\-()]+$/u;
// Codes d'aéroport et sigles (« BUH ») : jamais pris pour un nom de ville.
const CODE = /^[A-Z]{2,4}$/;

export const parseCityLine = (line: string): CompactCity | undefined => {
  const fields = line.split("\t");
  if (fields.length < 15) return undefined;
  const [id, name, ascii, alternates, lat, lon] = fields;
  const country = fields[8];
  if (!id || !name || !country) return undefined;
  const seen = new Set([name.toLowerCase(), ascii.toLowerCase()]);
  const names: string[] = [];
  for (const alternate of alternates.split(",")) {
    const value = alternate.trim();
    const lower = value.toLowerCase();
    if (!value || value.length > 60 || seen.has(lower)) continue;
    if (CODE.test(value) || !LATIN.test(value)) continue;
    seen.add(lower);
    names.push(value);
  }
  return [
    Number(id),
    name,
    ascii,
    Number(lat),
    Number(lon),
    country,
    fields[10] ?? "",
    Number(fields[14]) || 0,
    names,
  ];
};

export const geonamesFileOk = (name: string) =>
  /^cities1000(?: \(\d+\))?\.zip$/i.test(name.trim());

/** Semestre attendu (« 2027-01 », « 2027-07 »), à partir du 6 de janvier ou de juillet : même règle que le worker. */
export const semesterPeriod = (today: Date) => {
  let year = today.getFullYear();
  let month = today.getMonth() + 1 >= 7 ? 7 : 1;
  if (today.getMonth() + 1 === month && today.getDate() < DUE_DAY) {
    if (month === 7) month = 1;
    else {
      year -= 1;
      month = 7;
    }
  }
  return `${year}-${String(month).padStart(2, "0")}`;
};

export type CitiesProgress = {
  bytesRead: number;
  totalBytes: number;
  cities: number;
};

/** Lit cities1000.zip en flux et renvoie les villes compactes. */
export const readGeonamesZip = async (
  file: File,
  onProgress: (progress: CitiesProgress) => void,
): Promise<CompactCity[]> => {
  const cities: CompactCity[] = [];
  const decoder = new TextDecoder();
  let rest = "";
  let bytesRead = 0;
  let entries = 0;
  let failure: Error | undefined;
  const take = (text: string) => {
    const lines = (rest + text).split("\n");
    rest = lines.pop() ?? "";
    for (const line of lines) {
      const city = parseCityLine(line);
      if (city) cities.push(city);
    }
  };

  const unzip = new Unzip((entry) => {
    if (!entry.name.toLowerCase().endsWith(".txt")) return;
    entries += 1;
    entry.ondata = (error, chunk, final) => {
      if (error) failure = error;
      else {
        take(decoder.decode(chunk, { stream: !final }));
        if (final) take("\n");
      }
    };
    entry.start();
  });
  unzip.register(UnzipInflate);

  const reader = file.stream().getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (failure) {
      await reader.cancel();
      throw failure;
    }
    if (done) {
      unzip.push(new Uint8Array(0), true);
      break;
    }
    unzip.push(value);
    bytesRead += value.length;
    onProgress({ bytesRead, totalBytes: file.size, cities: cities.length });
  }
  if (failure) throw failure;
  if (entries === 0) throw new Error("no_txt");
  onProgress({ bytesRead, totalBytes: file.size, cities: cities.length });
  return cities;
};
